import { FormContext, FormItem } from "./form-builder.model";
import { notify } from "../notifications/NotificationProvider";
import { Dataset } from "../../model/qubes/dataset";
import { FieldDef } from "../../model/qubes/field";
import { FormValidator } from "./validation";
import { FormSection } from "./renderer/form-renderer";
import { datasetFormRequest } from "./dataset-form-signal";

/**
 * A per-field override (L1 knob). The field is referenced BY OBJECT (Model.field),
 * and `item` transforms the default FormItem the field's getFormItem produced —
 * add showWhen, swap to a widget/custom render, supply options, etc.
 */
export type FieldOverride = {
  field: FieldDef;
  item: (defaultItem: FormItem, field: FieldDef) => FormItem;
};

/** A layout section (L2): a title + the fields in it, referenced by object. */
export type FormSectionDef = { title: string; fields: FieldDef[] };

export type CreateFormOptions = {
  /** flat field list, BY REFERENCE — used when `sections` is not given */
  fields?: FieldDef[];
  /** L2 layout: grouped sections (reference fields by object) */
  sections?: FormSectionDef[];
  /** existing record key to edit; undefined = create a new record */
  key?: string;
  title?: string;
  submitLabel?: string;
  /** per-field L1 overrides, referenced by field object */
  overrides?: FieldOverride[];
  /** whole-form validator (submit-first; the renderer also runs field validators) */
  validate?: FormValidator;
  /** initial values for a NEW record (create mode); ignored when editing (key set) */
  defaults?: Record<string, unknown>;
  /** called with the written key after a successful save (e.g. refresh a table) */
  onSaved?: (key: string) => void;
  /** ambient lineage context (parent-view selections) — threaded to field
   *  validators and custom widgets/renders */
  context?: FormContext;
};

/**
 * Open a form for a Dataset — the L0 primitive. Builds the renderer spec from the
 * dataset + a field/section selection (+ overrides + validate) and pushes it to the
 * DatasetFormModal (the new renderer). Submit -> dataset.save. Fields hidden by a
 * showWhen predicate are excluded from the write.
 */
export async function createForm(
  dataset: Dataset,
  opts: CreateFormOptions = {},
): Promise<void> {
  const record = (await dataset.load(opts.key)) ?? {};
  const overrideByName = new Map((opts.overrides ?? []).map((o) => [o.field.name, o.item]));

  const buildItem = (field: FieldDef): FormItem => {
    const base = field.getFormItem(record, field);
    const ov = overrideByName.get(field.name);
    return ov ? ov(base, field) : base;
  };

  const sectionDefs: FormSectionDef[] = opts.sections ?? [
    { title: "", fields: opts.fields ?? dataset.fields },
  ];
  const sections: FormSection[] = sectionDefs.map((s) => ({
    title: s.title,
    items: s.fields.map(buildItem),
  }));

  const allItems = sections.flatMap((s) => s.items);
  // New-record defaults (create mode only): seed BEFORE field values so a loaded
  // record always wins, but a fresh form gets sensible starting values.
  const initialValues: Record<string, unknown> = opts.key ? {} : { ...(opts.defaults ?? {}) };
  for (const s of sectionDefs) {
    for (const f of s.fields) {
      const v = f.getValue(record, f);
      if (v !== undefined) initialValues[f.name] = v;
    }
  }

  datasetFormRequest.next({
    title: opts.title,
    sections,
    initialValues,
    validate: opts.validate,
    context: opts.context,
    submitLabel: opts.submitLabel,
    onCancel: () => datasetFormRequest.next(undefined),
    onSubmit: async (values) => {
      // Exclude fields hidden by their showWhen predicate — they must not be written.
      const toSave: Record<string, unknown> = {};
      for (const item of allItems) {
        if (item.showWhen && !item.showWhen(values)) continue;
        if (!(item.name in values)) continue;
        let v = values[item.name];
        // blankIsNull (declared on FormItem, honored here at the write
        // boundary): an empty string means "no value" for this column —
        // e.g. a nullable FK select whose placeholder option is "".
        if (item.blankIsNull && v === "") v = null;
        toSave[item.name] = v;
      }
      const res = await dataset.save(toSave, opts.key);
      if (res.success) {
        opts.onSaved?.(res.key);
        datasetFormRequest.next(undefined);
        notify({ severity: "success", title: "Saved", message: `${dataset.cube} saved.` });
      } else {
        // TODO (punch-list #5): bind res.error / server validationFailures per-field.
        notify({
          severity: "error",
          title: "Save failed",
          message: res.error?.message ?? `Failed to save ${dataset.cube}.`,
        });
      }
    },
  });
}
